import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  RadioGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@/components/ui";
import states from "@/constants/States";
import { TRACKERS } from "@/constants/Trackers";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { trackEvent } from "@/lib/tracking";
import type { Location, MedicationRequest } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { PharmacyOption } from "./PharmacySearchOption";
import {
  type PharmacySearchParams,
  usePharmacySearch,
} from "./network/usePharmacySearch";
import { useSetPreferredPharmacy } from "./network/useSetPreferredPharmacy";
import { PharmacyType, searchSchema } from "./schema";

interface PharmacyModalProps {
  patientId: string;
  open: boolean;
  medicationRequest?: MedicationRequest;
  onOpenChange(o: boolean): void;
  applyToCurrentMedication?: (pharmacy: Location) => void;
  applyToAllMedications?: (pharmacy: Location) => void;
}

const formatZipCode = (zip: string | undefined) => {
  if (!zip) return undefined;
  // Extract just the first 5 digits if it's a ZIP+4
  const match = zip.match(/^(\d{5})/);
  return match ? match[1] : undefined;
};

export function PharmacyModal({
  patientId,
  open,
  onOpenChange,
  medicationRequest,
  applyToCurrentMedication,
  applyToAllMedications,
}: PharmacyModalProps) {
  const [params, setParams] = useState<PharmacySearchParams | null>(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState<Location | null>(
    null,
  );
  const [applyAsPatientPreferred, setApplyAsPatientPreferred] = useState(true);
  const { data: patient } = useGetPatient(patientId);
  const { mutate: setPreferredPharmacy } = useSetPreferredPharmacy({
    patientId,
  });

  const {
    data: pharmacies,
    isLoading: isLoadingPharmacies,
    refetch,
  } = usePharmacySearch(params, {
    enabled: false,
  });

  const homeAddressState = patient?.homeAddress?.state;
  const homeAddressZip = patient?.homeAddress?.postalCode;
  const homeAddressCity = patient?.homeAddress?.city;

  const searchForm = useForm<z.infer<typeof searchSchema>>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      name: "",
      street: "",
      city: homeAddressCity,
      state: homeAddressState,
      zip: formatZipCode(homeAddressZip),
      phone: "",
      serviceLevels: PharmacyType.Retail,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Targeted to avoid infinite re-renders
  useEffect(() => {
    if (patient) {
      searchForm.reset({
        city: patient.homeAddress?.city,
        state: patient.homeAddress?.state,
        zip: formatZipCode(patient.homeAddress?.postalCode),
        serviceLevels: PharmacyType.Retail,
      });
    }
  }, [
    patient?.homeAddress?.city,
    patient?.homeAddress?.state,
    patient?.homeAddress?.postalCode,
    searchForm?.reset,
  ]);

  useEffect(() => {
    if (params) {
      trackEvent(TRACKERS.SEARCH_FOR_PHARMACY);
      refetch();
    }
  }, [params, refetch]);

  const onSubmitSearch = async (values: z.infer<typeof searchSchema>) => {
    setSelectedPharmacy(null);
    if (values.serviceLevels === PharmacyType.Retail) {
      setParams({
        name: values.name,
        city: values.city,
        state: values.state,
        zip: values.zip,
        address: values.street,
        phone: values.phone,
        serviceLevels: values.serviceLevels,
      });
    } else {
      setParams({
        name: values.name,
        city: undefined,
        state: undefined,
        zip: undefined,
        address: undefined,
        phone: undefined,
        serviceLevels: values.serviceLevels,
      });
    }
  };

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          // Prevents the dialog from closing when clicking outside of the content
          // This allows the user to scroll chat and copy text (like pharmacy addresses)
          onPointerDownOutside={(e) => e.preventDefault()}
          className="w-full max-w-3xl h-1/2 top-[33%] flex flex-col"
        >
          <DialogHeader className="h-fit">
            <DialogTitle>Pharmacy Search</DialogTitle>
            <DialogDescription className="hidden">
              pharmacy search modal
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-row overflow-hidden gap-8">
            <form
              onSubmit={searchForm.handleSubmit(onSubmitSearch)}
              className="grid grid-cols-2 auto-rows-min gap-2 w-1/3 h-full overflow-y-auto"
            >
              <Controller
                control={searchForm.control}
                name="serviceLevels"
                render={({ field, fieldState }) => (
                  <Field
                    data-invalid={fieldState.invalid}
                    className="col-span-2"
                  >
                    <FieldLabel htmlFor="serviceLevels">
                      Pharmacy Type
                    </FieldLabel>
                    <RadioGroup
                      value={field.value.toString()}
                      onValueChange={(value) => {
                        searchForm.clearErrors();
                        searchForm.setValue(
                          "serviceLevels",
                          Number.parseInt(value),
                          {
                            shouldValidate: false,
                          },
                        );
                      }}
                      options={[
                        {
                          value: PharmacyType.Retail.toString(),
                          label: "Retail",
                        },
                        {
                          value: PharmacyType.Mail.toString(),
                          label: "Mail Order",
                        },
                      ]}
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />

              <Controller
                control={searchForm.control}
                name="name"
                render={({ field, fieldState }) => (
                  <Field
                    data-invalid={fieldState.invalid}
                    className="col-span-2"
                  >
                    <FieldLabel htmlFor="pharmacyName">
                      Pharmacy Name
                    </FieldLabel>
                    <Input
                      {...field}
                      id="pharmacyName"
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />

              {searchForm.watch("serviceLevels") === PharmacyType.Retail && (
                <Fragment>
                  <Controller
                    control={searchForm.control}
                    name="street"
                    render={({ field, fieldState }) => (
                      <Field
                        data-invalid={fieldState.invalid}
                        className="col-span-2"
                      >
                        <FieldLabel htmlFor="pharmacyStreet">
                          Pharmacy Street
                        </FieldLabel>
                        <Input
                          {...field}
                          id="pharmacyStreet"
                          aria-invalid={fieldState.invalid}
                        />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                  <Controller
                    control={searchForm.control}
                    name="city"
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="pharmacyCity">
                          Pharmacy City
                        </FieldLabel>
                        <Input
                          {...field}
                          id="pharmacyCity"
                          aria-invalid={fieldState.invalid}
                        />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                  <Controller
                    control={searchForm.control}
                    name="state"
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="pharmacyState">
                          Pharmacy State
                        </FieldLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger aria-invalid={fieldState.invalid}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {states.map(({ name, abbreviation }) => (
                              <SelectItem
                                key={abbreviation}
                                value={abbreviation}
                              >
                                {name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                  <Controller
                    control={searchForm.control}
                    name="zip"
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="pharmacyZip">
                          Pharmacy ZIP
                        </FieldLabel>
                        <Input
                          {...field}
                          id="pharmacyZip"
                          type="number"
                          className="appearance-none"
                          aria-invalid={fieldState.invalid}
                        />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                  <Controller
                    control={searchForm.control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="pharmacyPhone">
                          Pharmacy Phone
                        </FieldLabel>
                        <Input
                          {...field}
                          id="pharmacyPhone"
                          aria-invalid={fieldState.invalid}
                        />
                        <FieldError errors={[fieldState.error]} />
                      </Field>
                    )}
                  />
                </Fragment>
              )}

              <Button
                size="sm"
                type="submit"
                className="w-fit"
                variant="secondary"
                isLoading={isLoadingPharmacies}
              >
                Search
              </Button>
            </form>

            <div className="flex flex-col w-2/3 gap-2">
              {isLoadingPharmacies && <Skeleton className="w-full h-14" />}
              {!isLoadingPharmacies &&
                pharmacies &&
                pharmacies.length === 0 && (
                  <Callout
                    variant="info"
                    title="No Results"
                    description="No pharmacies found near the given location."
                  />
                )}
              {!isLoadingPharmacies && pharmacies && pharmacies.length > 0 && (
                <>
                  <div className="flex flex-1 flex-col gap-2 overflow-auto">
                    {pharmacies.map((pharmacy) => (
                      <PharmacyOption
                        pharmacy={pharmacy}
                        key={pharmacy.id}
                        checked={selectedPharmacy?.id === pharmacy.id}
                        onChange={() => setSelectedPharmacy(pharmacy)}
                      />
                    ))}
                  </div>
                  {!applyToAllMedications && !applyToCurrentMedication && (
                    <Button
                      size="sm"
                      className="w-fit ml-auto"
                      disabled={!selectedPharmacy}
                      onClick={() => {
                        setPreferredPharmacy({
                          pharmacyId:
                            selectedPharmacy?.identifier?.[0].value ?? "",
                        });
                        onOpenChange(false);
                      }}
                    >
                      Save Preferred Pharmacy
                    </Button>
                  )}
                  {(applyToAllMedications || applyToCurrentMedication) && (
                    <div className="flex flex-row gap-2">
                      <div className="flex flex-1 flex-row items-center gap-2">
                        <Checkbox
                          id="applyAsPatientPreferred"
                          checked={applyAsPatientPreferred}
                          onCheckedChange={(checked) =>
                            setApplyAsPatientPreferred(
                              checked === "indeterminate" ? false : checked,
                            )
                          }
                        />
                        <Label htmlFor="applyAsPatientPreferred">
                          Set as patient preferred
                        </Label>
                      </div>
                      <div className="flex flex-row gap-2 justify-end">
                        {medicationRequest && (
                          <Button
                            size="sm"
                            className="w-fit ml-auto"
                            disabled={!selectedPharmacy}
                            onClick={() => {
                              trackEvent(
                                TRACKERS.USER_APPLIES_PHARMACY_TO_CURRENT_MEDICATION,
                              );
                              if (applyAsPatientPreferred) {
                                setPreferredPharmacy({
                                  pharmacyId:
                                    selectedPharmacy?.identifier?.[0].value ??
                                    "",
                                });
                              }
                              applyToCurrentMedication?.(
                                selectedPharmacy as Location,
                              );
                            }}
                          >
                            Apply to{" "}
                            {medicationRequest.medicationName.split(" ")[0]}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          className="w-fit ml-auto"
                          disabled={!selectedPharmacy}
                          onClick={() => {
                            trackEvent(
                              TRACKERS.USER_ADDS_PHARMACY_TO_UNSENT_PRESCRIPTIONS,
                            );
                            if (applyAsPatientPreferred) {
                              setPreferredPharmacy({
                                pharmacyId:
                                  selectedPharmacy?.identifier?.[0].value ?? "",
                              });
                            }
                            applyToAllMedications?.(
                              selectedPharmacy as Location,
                            );
                          }}
                        >
                          Apply to unsent prescriptions
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
